import { useEffect, useState } from "react";
import type { PlotParams } from "react-plotly.js";

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type PlotlyType = typeof import("plotly.js-dist-min/index");
type PlotType = React.ComponentType<PlotParams>;

/**
 * Dynamically load Plotly library since it isn't SSR compatible.
 */
export function usePlotly() {
  // Cache plotly.js library and Plot React component in local state
  const [plotly, setPlotly] = useState<PlotlyType>();
  const [Plot, setPlot] = useState<PlotType>();

  useEffect(() => {
    // Plotly.js does not support SSR so we need to dynamically import via useEffect.
    const setupPlotly = async () => {
      const [plotlyLoad, factoryLoad] = await Promise.all([
        // @ts-expect-error
        import("plotly.js-strict-dist-min"),
        import("react-plotly.js/factory"),
      ]);
      const plotlyDefault = plotlyLoad.default;

      // Bundler (vite/rolldown) can single wrap or double wrap cjs exports
      // defensively check for both
      const createPlotlyComponent = ((factoryLoad.default as any)?.default ||
        factoryLoad.default) as (plotly: PlotlyType) => React.ComponentType<PlotParams>;
      const PlotComponent = createPlotlyComponent(plotlyDefault);

      setPlotly(plotlyDefault);
      // Since PlotComponent is a function, must wrap in a function to store in state
      setPlot(() => PlotComponent);
    };
    setupPlotly();
  }, []);

  return [plotly, Plot] as [PlotlyType, PlotType];
}
